import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Pencil } from "lucide-react"
import { useMemo } from "react"
import type { ConditionFieldRow } from "../../../../../api/types"
import { conditionSpec } from "../../studio/conditions"
import { substanceGapOpen } from "../mapping/openPointDetail"
import { readBindings } from "../mapping/reviewerBindings"
import { useConditionRows } from "../reviewMode"
import type { StatementCard } from "../useStudioV2State"
import { substanceDisplayName } from "./substanceDisplayName"
import {
  cellState,
  concentrationDisplay,
  entryArticles,
  substanceEntries,
  type SubstanceEntry,
} from "./substanceTable"

export interface SubstanceRowProps {
  declId: string
  card: StatementCard
  /** Opens the substance table in place of the cards (`ReviewMaskPane`'s
   *  sheet, round 6): the PDF stays beside it. A chip passes its own
   *  `SubstanceChip.key`, the edit button passes null. Omitted (a caller
   *  with no pane to host the sheet, every test predating it): the chips
   *  stay plain labels and the edit button is not offered, the same
   *  convention as `UnitCard`'s `onOpenArticleBrowser`. */
  onOpenEditor?: (focusKey: string | null) => void
}

export interface SubstanceChip {
  /** `SubstanceEntry.key`: the editor opens with this substance's rows. */
  key: string
  /** Display name, or `null` when only the registry knows it (an added
   *  substance): the chip then shows the CAS alone. */
  name: string | null
  cas: string | null
  /** The glance value: the ONE persisted concentration when every article
   *  agrees, else the extracted value(s) while nothing is persisted yet;
   *  `mixed` names how many values the articles carry when they differ. */
  concentration: string | null
  mixed: number | null
  articleCount: number
}

/**
 * One chip per DISTINCT substance the statement declares (never one per
 * disclosure, never one per article), folded by the same persistence
 * identity the editor groups on (`substanceEntries`: normalised CAS), plus
 * every substance the reviewer added by hand (this statement's own reviewer
 * binding). `rows` are the persisted condition rows, for the glance value.
 */
export function substanceChips(
  card: StatementCard,
  rows: readonly ConditionFieldRow[] = [],
  translate: (key: TranslationKeys) => string = () => "",
): SubstanceChip[] {
  const bindings = readBindings(rows, card.framework, card.id)
  const spec = conditionSpec(card.framework)
  const entries = substanceEntries(card.conditions, bindings)
  return entries
    .filter((e) => e.cas !== null || e.names.length > 0)
    .map((entry) => chipOf(entry, card, rows, bindings, spec, translate))
}

function chipOf(
  entry: SubstanceEntry,
  card: StatementCard,
  rows: readonly ConditionFieldRow[],
  bindings: ReadonlyMap<string, string[]>,
  spec: ReturnType<typeof conditionSpec>,
  translate: (key: TranslationKeys) => string,
): SubstanceChip {
  const articles = entryArticles(
    entry,
    card.editorArticles ?? card.resolvedArticles,
    bindings,
  )
  const persisted = spec.concentration
    ? cellState(rows, articles, {
        substanceCas: entry.cas,
        framework: null,
        fieldKey: "concentration",
      })
    : null
  const name =
    entry.names.length > 0
      ? substanceDisplayName(entry.names[0], entry.cas, translate) ||
        entry.names[0]
      : substanceDisplayName("", entry.cas, translate) || null
  return {
    key: entry.key,
    name,
    cas: entry.cas,
    concentration:
      persisted && persisted.shared
        ? concentrationDisplay(persisted.shared)
        : (entry.concentrations[0] ?? null),
    mixed:
      persisted && persisted.shared === null ? persisted.distinct.length : null,
    articleCount: articles.length,
  }
}

/**
 * The STOFFE row of an expanded unit card (review-mask prototype, round 5):
 * one chip per substance with the facts that matter at a glance (name, CAS,
 * the concentration, how many articles it binds) and the substance table
 * behind it (`SubstanceEditorSheet` in place of the cards since round 6:
 * it used to be a blocking dialog, but the reviewer copies values off the
 * PDF, so the document pane must stay live). A chip opens the table with
 * that substance's per-article rows open; the button opens it plain. Only
 * rendered while the verdict names an exception (`UnitCard`'s
 * `CONDITIONED_VERDICTS`).
 */
export function SubstanceRow({
  declId,
  card,
  onOpenEditor,
}: SubstanceRowProps) {
  const { translate } = useTranslation()
  // The row reads the persisted rows for the chips' glance values only; the
  // editor behind it owns every write (`useConditionFieldWrites`).
  const { data } = useConditionRows(declId)
  const persistedRows = data?.rows
  const chips = useMemo(
    () => substanceChips(card, persistedRows ?? [], translate),
    [card, persistedRows, translate],
  )

  return (
    <div className="sv2-uslot sv2-dark" data-testid="substance-row">
      <div className="sv2-slot">
        <span className="sv2-ski">
          {translate("MC_DECL_V2_UNIT_SUBSTANCES_LABEL")}
        </span>
        <div className="sv2-slotbody">
          <div className="sv2-slotline">
            {chips.map((chip) => {
              const gapOpen = substanceGapOpen(card.openDetail, chip)
              const body = (
                <>
                  <span className="sv2-sdot" aria-hidden="true" />
                  <span className="sv2-sname">{chip.name ?? chip.cas}</span>
                  {chip.cas && chip.name && (
                    <span className="sv2-smeta">{chip.cas}</span>
                  )}
                  {chip.mixed !== null ? (
                    <>
                      <span className="sv2-ssep">·</span>
                      <span className="sv2-smeta">
                        {translate("MC_DECL_V2_SUBST_MIXED", { n: chip.mixed })}
                      </span>
                    </>
                  ) : (
                    chip.concentration && (
                      <>
                        <span className="sv2-ssep">·</span>
                        <span className="sv2-smeta">{chip.concentration}</span>
                      </>
                    )
                  )}
                  <span className="sv2-ssep">·</span>
                  <span className="sv2-smeta">
                    {translate("MC_DECL_V2_UNIT_SUBSTANCE_ARTICLES", {
                      n: chip.articleCount,
                    })}
                  </span>
                  {gapOpen && (
                    <span className="sv2-sreq">
                      {translate("MC_DECL_V2_CHIP_REQUIRED_OPEN")}
                    </span>
                  )}
                </>
              )
              const chipClass = cn("sv2-schip", gapOpen && "sv2-schip-gap")
              return onOpenEditor ? (
                <button
                  key={chip.key}
                  type="button"
                  className={chipClass}
                  data-gap={gapOpen || undefined}
                  onClick={() => onOpenEditor(chip.key)}
                  title={translate("MC_DECL_V2_UNIT_SUBSTANCES_EDIT")}
                >
                  {body}
                </button>
              ) : (
                <span
                  key={chip.key}
                  className={chipClass}
                  data-gap={gapOpen || undefined}
                >
                  {body}
                </span>
              )
            })}
            {chips.length === 0 && (
              <span className="sv2-hint">
                {translate("MC_DECL_V2_UNIT_SUBSTANCES_NONE")}
              </span>
            )}
            {onOpenEditor && (
              <button
                type="button"
                className="sv2-hact sv2-hact-ghost"
                onClick={() => onOpenEditor(null)}
              >
                <Pencil className="sv2-ic" />
                {translate("MC_DECL_V2_UNIT_SUBSTANCES_EDIT")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
